/**
 * ALN bulk import — parity with Angular `import-data.component.ts` API usage.
 */
import { format } from 'date-fns'
import { env } from '@config/env'
import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import type {
  ImportDataDraftAction,
  ImportFilterState,
  AlnRow,
  AddDraftResponse,
  LibertyUserOption,
} from '../types'

export type { LibertyUserOption } from '../types'

function filterToPlain(f: ImportFilterState): Record<string, string> {
  const o: Record<string, string> = {}
  if (f.type) o.type = f.type
  if (f.fk_team_id) o.fk_team_id = f.fk_team_id
  if (f.listed) o.listed = f.listed
  if (f.pmc_import_type && ['pmc_import', 'property_import'].includes(f.type)) {
    o.pmc_import_type = f.pmc_import_type
  }
  if (f.dateStart && f.dateEnd) {
    o.fromDt = format(f.dateStart, 'dd-MM-yyyy')
    o.toDt = format(f.dateEnd, 'dd-MM-yyyy')
  }
  return o
}

/** Angular `onSubmit` only sends `type` + `file` to `/api/aln/import_changes`. */
function appendImportChangesOnly(fd: FormData, f: ImportFilterState) {
  if (f.type) fd.append('type', f.type)
}

/** Angular `add_draft` (upload) only includes `type`, `file`, and action extras — not listed/team/dates. */
function appendAddDraftFormData(
  fd: FormData,
  f: ImportFilterState,
  extra: Partial<{
    action_type: ImportDataDraftAction
    selected_id: (number | string)[]
    proceed: boolean
  }> = {},
) {
  if (f.type) fd.append('type', f.type)
  if (extra.action_type) fd.append('action_type', extra.action_type)
  if (extra.selected_id?.length) fd.append('selected_id', JSON.stringify(extra.selected_id))
  if (extra.proceed) fd.append('proceed', 'true')
}

export async function postImportChanges(file: File | null, f: ImportFilterState): Promise<unknown> {
  try {
    const fd = new FormData()
    appendImportChangesOnly(fd, f)
    if (file) fd.append('file', file)
    const res = await apiClient.post<unknown>('/api/aln/import_changes', fd)
    return res.data
  } catch (e) {
    throw new Error(extractErrorMessage(e) || 'Import failed')
  }
}

export function buildAlnListParams(f: ImportFilterState): Record<string, string> {
  return filterToPlain(f)
}

export async function getAlnSync(f: ImportFilterState): Promise<AlnRow[]> {
  try {
    const params = buildAlnListParams(f)
    const res = await apiClient.get<AlnRow[]>('/api/aln/aln_sync', { params })
    return Array.isArray(res.data) ? res.data : []
  } catch (e) {
    throw new Error(extractErrorMessage(e) || 'Failed to load ALN sync list')
  }
}

export async function getAlnHistory(f: ImportFilterState): Promise<AlnRow[]> {
  try {
    const params = buildAlnListParams(f)
    const res = await apiClient.get<AlnRow[]>('/api/aln/aln_history', { params })
    return Array.isArray(res.data) ? res.data : []
  } catch (e) {
    throw new Error(extractErrorMessage(e) || 'Failed to load import history')
  }
}

/** ALN Upload tab — multipart `add_draft` (Angular: `upload: true`). */
export async function postAddDraft(
  f: ImportFilterState,
  file: File | null,
  extra: Partial<{
    action_type: ImportDataDraftAction
    selected_id: (number | string)[]
    proceed: boolean
  }> = {},
): Promise<AddDraftResponse> {
  try {
    const fd = new FormData()
    appendAddDraftFormData(fd, f, extra)
    if (file) fd.append('file', file)
    const res = await apiClient.post<AddDraftResponse>('/api/aln/add_draft', fd)
    return (res.data ?? {}) as AddDraftResponse
  } catch (e) {
    throw new Error(extractErrorMessage(e) || 'Apply failed')
  }
}

/** ALN Sync / History — JSON `update_draft` (Angular: no `upload: true` on that POST). */
export async function postUpdateDraft(
  f: ImportFilterState,
  body: {
    action_type: ImportDataDraftAction
    selected_id: (number | string)[]
    proceed?: boolean
  },
): Promise<AddDraftResponse> {
  try {
    const payload: Record<string, unknown> = {
      type: f.type,
      action_type: body.action_type,
      selected_id: body.selected_id,
    }
    if (body.proceed) {
      payload.proceed = true
    }
    const res = await apiClient.post<AddDraftResponse>('/api/aln/update_draft', payload)
    return (res.data ?? {}) as AddDraftResponse
  } catch (e) {
    throw new Error(extractErrorMessage(e) || 'Update failed')
  }
}

export async function searchLibertyUsers(searchText: string): Promise<LibertyUserOption[]> {
  try {
    const params: Record<string, string> = { organization_type: 'LIBERTY' }
    const q = searchText.trim()
    if (q) params.username = q
    const res = await apiClient.get<{ data?: Array<{ user_id: number | string; name?: string }> }>(
      '/api/user',
      { params },
    )
    const rows = res.data?.data
    if (!Array.isArray(rows)) return []
    return rows.map((u) => ({
      user_id: u.user_id,
      value: String(u.user_id),
      label: u.name ?? String(u.user_id),
      name: u.name,
    }))
  } catch (e) {
    throw new Error(extractErrorMessage(e) || 'User search failed')
  }
}

/**
 * Angular `EmailOptionComponent.exportSubmit`: `window.open` GET `/api/aln/export?access_token=…&page=…&type=…`
 * with optional `fromDt` / `toDt` (`DD-MM-YYYY`).
 */
export function buildAlnExportUrl(
  page: string,
  importType: string,
  dateStart?: Date | null,
  dateEnd?: Date | null,
): string | null {
  const token = getAuthTokenFromStorage()
  if (!token) return null
  const apiBase = import.meta.env.DEV ? window.location.origin : env.apiBaseUrl.replace(/\/$/, '')
  const url = new URL('/api/aln/export', apiBase)
  url.searchParams.set('access_token', token)
  url.searchParams.set('page', page)
  url.searchParams.set('type', importType)
  if (dateStart && dateEnd) {
    url.searchParams.set('fromDt', format(dateStart, 'dd-MM-yyyy'))
    url.searchParams.set('toDt', format(dateEnd, 'dd-MM-yyyy'))
  }
  return url.toString()
}

/** Angular `EmailOptionComponent.submitForm` → POST `/api/aln/aln_mail` as FormData (`upload: true`). */
export async function postAlnMail(formData: FormData): Promise<void> {
  try {
    await apiClient.post('/api/aln/aln_mail', formData)
  } catch (e) {
    throw new Error(extractErrorMessage(e) || 'Failed to send mail')
  }
}
